import { zodResolver } from "@hookform/resolvers/zod";
import {
	Calendar,
	Car,
	Check,
	Mail,
	MessageSquare,
	Phone,
	User,
} from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CustomDatePicker } from "@/components/ui/custom-date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ServiceOption, VehicleClass } from "@/types";

const contactFormSchema = z.object({
	name: z.string().min(2, "Name ist erforderlich (min. 2 Zeichen)"),
	email: z.string().email("Ungültige E-Mail-Adresse"),
	phone: z.string().min(5, "Telefonnummer ist erforderlich"),
	car: z.string().min(1, "Bitte wählen Sie eine Fahrzeugklasse"),
	model: z.string().min(2, "Fahrzeugmodell ist erforderlich"),
	service: z.string().min(1, "Bitte wählen Sie eine Leistung"),
	date: z.date().optional(),
	message: z.string().optional(),
	website: z.string().optional(),
	privacy: z.boolean().refine((val) => val === true, {
		message: "Bitte akzeptieren Sie die Datenschutzerklärung",
	}),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const vehicleClasses: readonly VehicleClass[] = [
	{ value: "Motorrad", label: "Motorrad" },
	{
		value: "Kleinwagen",
		label: "Kleinwagen (z.B. Smart, Opel Corsa)",
	},
	{
		value: "Mittelklassewagen",
		label: "Mittelklassewagen (z.B. VW ID3, Renault Clio)",
	},
	{
		value: "Limousinen",
		label: "Limousinen (z.B. Mercedes S Klasse, Audi A4)",
	},
	{ value: "SUV", label: "SUV (z.B. Audi Q4-Q7)" },
	{ value: "Van - Bus bis 9 Sitzer", label: "Van - Bus bis 9 Sitzer" },
	{ value: "Wohnmobil", label: "Wohnmobil" },
] as const;

const serviceOptions: readonly ServiceOption[] = [
	{ value: "Beratung", label: "Beratung" },
	{ value: "1", label: "Handwäsche" },
	{ value: "2", label: "Innenraumreinigung" },
	{
		value: "3",
		label: "Handwäsche + Innenraumreinigung",
	},
	{
		value: "4",
		label: "Handwäsche + Innenraumreinigung + Polsterreinigung",
	},
	{
		value: "5",
		label: "Handwäsche + Innenraumreinigung + Polsterreinigung + Felgen",
	},
	{
		value: "6",
		label: "Handwäsche + Innenraumreinigung + Polsterreinigung + Versiegeln",
	},
	{ value: "7", label: "Individuell" },
] as const;

const Contact: React.FC = () => {
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	const [formStartedAt] = useState<number>(() => Date.now());

	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<ContactFormValues>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			car: "Kleinwagen",
			model: "",
			service: "Beratung",
			date: undefined,
			message: "",
			website: "",
			privacy: false,
		},
	});

	const onSubmit = async (data: ContactFormValues): Promise<void> => {
		try {
			const response = await fetch("/api/contact.php", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					...data,
					formStartedAt,
					date: data.date ? format(data.date, "dd.MM.yyyy") : "", // Datum formatieren falls vorhanden
				}),
			});

			if (response.ok) {
				setIsSubmitted(true);
				reset();
			} else {
				const errorData = await response.json();
				alert(errorData.message || "Es gab ein Problem beim Senden.");
			}
		} catch (error) {
			console.error("Form error:", error);
			alert("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
		}
	};

	if (isSubmitted) {
		return (
			<div className="text-center py-20 px-4 min-h-[400px] flex flex-col justify-center">
				<div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
					<Check className="w-10 h-10 text-white" />
				</div>
				<h3 className="text-2xl font-bold mb-4">
					Vielen Dank für Ihre Anfrage!
				</h3>
				<p className="text-white/80">
					Wir haben Ihre Nachricht erhalten und werden uns so schnell wie
					möglich bei Ihnen melden.
				</p>
				<Button
					onClick={() => setIsSubmitted(false)}
					className="mt-8 mx-auto bg-black text-white hover:bg-black/80"
				>
					Zurück zum Formular
				</Button>
			</div>
		);
	}

	return (
		<section
			id="contact"
			className="py-20 text-black"
			style={{
				background:
					"linear-gradient(to bottom, rgba(207, 194, 151, 0.3), rgba(207, 194, 151, 0.3)), white",
			}}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Contact Info */}
				<div className="text-center mb-12">
					<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 uppercase">
						Nimm Kontakt zu uns auf...
					</h2>
					<p className="text-lg mb-6">
						und wir melden uns schnellstmöglich bei dir.
					</p>

					<div className="flex flex-wrap justify-center gap-8 text-lg">
						<a
							href="tel:01703571907"
							className="flex items-center gap-2 text-black hover:text-[#5a4a35] transition-colors"
						>
							<Phone className="w-5 h-5" />
							0170 3571907
						</a>
						<a
							href="mailto:daniel.wildfoerster@gmail.com"
							className="flex items-center gap-2 text-black hover:text-[#5a4a35] transition-colors"
						>
							<Mail className="w-5 h-5" />
							daniel.wildfoerster@gmail.com
						</a>
					</div>
				</div>

				{/* Contact Form */}
				<div className="max-w-4xl mx-auto">
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Name */}
							<FormField
								id="name"
								label="Name"
								required
								icon={<User className="w-4 h-4" />}
								error={errors.name?.message}
							>
								<Input
									id="name"
									{...register("name")}
									className={`bg-white/80 border-0 ${
										errors.name ? "ring-2 ring-red-500" : ""
									}`}
									placeholder="Ihr vollständiger Name"
								/>
							</FormField>

							{/* Email */}
							<FormField
								id="email"
								label="E-Mail"
								required
								icon={<Mail className="w-4 h-4" />}
								error={errors.email?.message}
							>
								<Input
									id="email"
									type="email"
									{...register("email")}
									className={`bg-white/80 border-0 ${
										errors.email ? "ring-2 ring-red-500" : ""
									}`}
									placeholder="ihre@email.de"
								/>
							</FormField>

							{/* Phone */}
							<FormField
								id="phone"
								label="Telefon"
								required
								icon={<Phone className="w-4 h-4" />}
								error={errors.phone?.message}
							>
								<Input
									id="phone"
									type="tel"
									{...register("phone")}
									className={`bg-white/80 border-0 ${
										errors.phone ? "ring-2 ring-red-500" : ""
									}`}
									placeholder="Ihre Telefonnummer"
								/>
							</FormField>

							{/* Vehicle Class */}
							<FormField
								id="car"
								label="Fahrzeugklasse"
								required
								icon={<Car className="w-4 h-4" />}
								error={errors.car?.message}
							>
								<Controller
									name="car"
									control={control}
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<SelectTrigger className="bg-white/80 border-0 h-10 font-medium text-gray-900">
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="bg-white border-gray-100 shadow-xl">
												{vehicleClasses.map((vc) => (
													<SelectItem
														key={vc.value}
														value={vc.value}
														className="text-gray-700 focus:bg-gray-100 focus:text-black"
													>
														{vc.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</FormField>

							{/* Model */}
							<FormField
								id="model"
								label="Fahrzeugmodell"
								required
								icon={<Car className="w-4 h-4" />}
								error={errors.model?.message}
							>
								<Input
									id="model"
									{...register("model")}
									className={`bg-white/80 border-0 ${
										errors.model ? "ring-2 ring-red-500" : ""
									}`}
									placeholder="z.B. Audi A4"
								/>
							</FormField>

							{/* Service */}
							<FormField
								id="service"
								label="Gewünschte Leistung"
								required
								icon={<SparklesIcon />}
								error={errors.service?.message}
							>
								<Controller
									name="service"
									control={control}
									render={({ field }) => (
										<Select value={field.value} onValueChange={field.onChange}>
											<SelectTrigger className="bg-white/80 border-0 h-10 font-medium text-gray-900">
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="max-h-[300px] bg-white border-gray-100 shadow-xl">
												{serviceOptions.map((so) => (
													<SelectItem
														key={so.value}
														value={so.value}
														className="text-gray-700 focus:bg-gray-100 focus:text-black"
													>
														{so.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</FormField>
						</div>

						{/* Desired Date */}
						<FormField
							id="date"
							label="Wunschtermin"
							icon={<Calendar className="w-4 h-4" />}
							error={errors.date?.message}
						>
							<Controller
								name="date"
								control={control}
								render={({ field }) => (
									<CustomDatePicker
										date={field.value}
										onSelect={field.onChange}
										placeholder="Datum auswählen"
									/>
								)}
							/>
						</FormField>

						{/* Message */}
						<FormField
							id="message"
							label="Nachricht"
							icon={<MessageSquare className="w-4 h-4" />}
							error={errors.message?.message}
						>
							<Textarea
								id="message"
								{...register("message")}
								className="bg-white/80 border-0 min-h-[120px]"
								placeholder="Haben Sie besondere Wünsche oder Anmerkungen?"
							/>
						</FormField>

						{/* Honeypot field - Bot protection */}
						<div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
							<input
								type="text"
								{...register("website")}
								tabIndex={-1}
								autoComplete="off"
							/>
						</div>

						{/* Privacy Checkbox */}
						<div className="flex items-start space-x-3">
							<Controller
								name="privacy"
								control={control}
								render={({ field }) => (
									<Checkbox
										id="privacy"
										checked={field.value}
										onCheckedChange={field.onChange}
										className={`mt-1 ${
											errors.privacy ? "ring-2 ring-red-500" : ""
										}`}
									/>
								)}
							/>
							<div>
								<Label
									htmlFor="privacy"
									className="text-sm font-normal cursor-pointer"
								>
									Ich stimme den{" "}
									<a
										href="/datenschutz"
										className="text-[#8b7355] hover:underline font-medium"
									>
										Datenschutzbestimmungen
									</a>{" "}
									zu. *
								</Label>
								{errors.privacy && (
									<p className="text-red-600 text-sm mt-1">
										{errors.privacy.message}
									</p>
								)}
							</div>
						</div>

						{/* Submit Button */}
						<div className="flex justify-end pt-4">
							<Button
								type="submit"
								disabled={isSubmitting}
								className="bg-black hover:bg-black/80 text-white px-8 py-6 text-lg"
							>
								{isSubmitting ? "Wird gesendet..." : "Anfrage senden"}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</section>
	);
};

// Sub-components
interface FormFieldProps {
	id: string;
	label: string;
	required?: boolean;
	icon?: React.ReactNode;
	error?: string | null | undefined;
	children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
	id,
	label,
	required,
	icon,
	error,
	children,
}) => (
	<div className="space-y-2">
		<Label htmlFor={id} className="flex items-center gap-2">
			{icon}
			{label}
			{required && " *"}
		</Label>
		{children}
		{error && <p className="text-red-600 text-sm mt-1">{error}</p>}
	</div>
);

const SparklesIcon: React.FC = () => (
	<svg
		className="w-4 h-4"
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
		<path d="M5 3v4" />
		<path d="M19 17v4" />
		<path d="M3 5h4" />
		<path d="M17 19h4" />
	</svg>
);

export default Contact;
